import { MultilingualLanguages } from '@asgard/common-dto';
import { Button, Stack } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import ConfirmModal from 'components/modals/ConfirmModal';
import FormGroup from 'components/shared/FormGroup';
import RadioButton from 'components/shared/RadioButton';
import Switcher from 'components/shared/Switcher';
import { PortalHeader } from 'features/portal/v2/components/layout/PortalHeader/PortalHeader';
import {
  StyledFieldset,
  StyledForm,
  StyledLegend
} from 'features/portal/v2/components/StyledForm';
import { useI18n } from 'hooks/useI18n';
import { useCallback, useState } from 'react';

import { useToasts } from 'hooks/useToasts';
import { ServiceManfredite } from 'services/service.manfredite';
import { getErrorMessage } from 'utils/errors';
import { PortalSettingsAccountPassword } from './password';
import { usePortalSettingsAccount } from './PortalSettingsAccount.hooks';
import { StyledRadioInline } from './PortalSettingsAccount.styled';
import { PortalSettingsAccountProps } from './PortalSettingsAccount.types';

export const PortalSettingsAccount: React.FC<PortalSettingsAccountProps> = ({
  manfrediteEmail
}) => {
  const { t } = useI18n('portal-settings');
  const { addErrorToast, addSuccessToast } = useToasts();

  const [isConfirmChangeEmailModalOpened, setIsConfirmChangeEmailModalOpened] =
    useState(false);

  const {
    handleSubmit,
    isSubmitting,
    lang,
    setLang,
    setSubscribed,
    subscribed
  } = usePortalSettingsAccount();

  const onConfirmChangeEmail = useCallback(async () => {
    try {
      await ServiceManfredite.requestChangeEmail();

      addSuccessToast(
        t('portal-contact:portal-contact.messages.success.email-change-request')
      );
    } catch (ex) {
      addErrorToast(
        t('portal-contact:portal-contact.messages.error.email-change-request', {
          error: getErrorMessage(ex)
        })
      );
    }
  }, [addErrorToast, addSuccessToast, t]);

  return (
    <StyledForm onSubmit={(e) => e.preventDefault()}>
      <PortalHeader title={t('portal-settings.account.title')} />

      <StyledFieldset>
        <StyledLegend>
          {t('portal-settings.account.form.email.title')}
        </StyledLegend>

        <FormGroup label={t('portal-settings.account.form.email.fields.email')}>
          <Stack alignment="center">
            <span>{manfrediteEmail}</span>

            <Button
              type="button"
              size="2xs"
              label={t('portal-contact:portal-contact.change-email')}
              variant="ghost"
              onClick={() => setIsConfirmChangeEmailModalOpened(true)}
            />
          </Stack>
        </FormGroup>

        <FormGroup>
          <Switcher
            id="subscribe"
            label={t('portal-settings.account.form.email.fields.ping')}
            checked={subscribed}
            onChange={() => setSubscribed((prev) => !prev)}
          />
        </FormGroup>
      </StyledFieldset>

      <PortalSettingsAccountPassword manfrediteEmail={manfrediteEmail} />

      <StyledFieldset>
        <StyledLegend>
          {t('portal-settings.account.form.languages.title')}
        </StyledLegend>
        <FormGroup
          label={t('portal-settings.account.form.languages.fields.language')}
        >
          <StyledRadioInline>
            <RadioButton
              id="EN"
              name="language"
              label={t('portal-settings.account.form.languages.fields.EN')}
              value="EN"
              checked={lang === 'EN'}
              onChange={(e) =>
                setLang(e.currentTarget.value as MultilingualLanguages)
              }
            />

            <RadioButton
              id="ES"
              name="language"
              label={t('portal-settings.account.form.languages.fields.ES')}
              value="ES"
              checked={lang === 'ES'}
              onChange={(e) =>
                setLang(e.currentTarget.value as MultilingualLanguages)
              }
            />
          </StyledRadioInline>
        </FormGroup>

        <Button
          onClick={handleSubmit}
          isLoading={isSubmitting}
          schema="neutral"
          variant="hard"
          startIcon={faCheckCircle}
          label={
            isSubmitting
              ? t('saving', { ns: 'common' })
              : t('save', { ns: 'common' })
          }
        />
      </StyledFieldset>

      <ConfirmModal
        title={t(
          'portal-contact:portal-contact.confirm-mail-change-modal.title'
        )}
        text={t('portal-contact:portal-contact.confirm-mail-change-modal.desc')}
        isOpen={isConfirmChangeEmailModalOpened}
        close={() => setIsConfirmChangeEmailModalOpened(false)}
        action={onConfirmChangeEmail}
        closeAfterConfirm
      />
    </StyledForm>
  );
};

export default PortalSettingsAccount;
